<template>
  <div id="outcome" class="hub-grey">
    <header class="top">
      <h1 class="title">Completion</h1>
      <h1 class="subtitle hub-grey">Please submit when you are ready to close your client file</h1>
    </header>
    <section class="content">
      <ul class="client-status-list">
        <li
          v-for="status in statusList"
          :key="status.id"
          :class="{
            selected: selectedStatus && status.meta_code === selectedStatus.meta_code,
            enabled: !closed,
          }"
          @click="selectStatus(status)"
          @keypress.enter="selectStatus(status)"
        >
          <h2>{{ $filters.humanReadableStatus(status.meta_code) }}</h2>
          <p :class="selectedStatus && status.meta_code === selectedStatus.meta_code ? 'circle-green' : 'circle'" />
        </li>
      </ul>
    </section>
    <section class="footer">
      <button type="button" :class="{ submit: true, enabled: hasChange }" @click="saveStatus">Submit</button>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';

import ClientService from '@/services/client';
import MetaService from '@/services/meta';
import useSessionStore from '@/stores/session';

const $router = useRouter();
const SessionStore = useSessionStore();
const statusList = ref([]);
const selectedStatus = ref(null);

const currentClient = computed(() => {
  return SessionStore.HUBSettings.currentClient;
});

const closed = computed(() => {
  return currentClient.value.closed;
});

const hasChange = computed(() => {
  return selectedStatus.value && selectedStatus.value.meta_code !== currentClient.value.status_meta_code;
});

async function getClientStatusList() {
  const metaResults = await MetaService.getMetasByType('client_status');
  statusList.value = metaResults.records.filter((r) => r.meta_code !== 'OPN');
  selectedStatus.value = statusList.value.find((st) => st.meta_code === currentClient.value.status_meta_code);
}

function selectStatus(status) {
  if (closed.value) {
    return;
  }
  selectedStatus.value = status;
}

async function saveStatus() {
  if (!hasChange.value) {
    return;
  }

  await ClientService.closeClient(currentClient.value.id, {
    status_meta_id: selectedStatus.value.id,
    status_meta_value: selectedStatus.value.meta_value,
  });

  $router.push(SessionStore.HUBSettings.levelUp);
}

watch(
  () => currentClient.value.id,
  () => {
    getClientStatusList();
  },
  { immediate: true }
);
</script>

<style scoped>
#outcome {
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;

  box-sizing: border-box;
  margin: 0 auto;
  width: 100%;
  min-height: 100%;
  padding: 2em 0.5em;
  font-size: 1.2em;
}

.top {
  box-sizing: border-box;
  width: 100%;
  padding: 0 1em;
}
.top > h1.title {
  margin-bottom: 0;
}
.top > h1.subtitle {
  margin-top: 0;
  font-weight: 400;
}

.content {
  margin-top: 2em;
  width: 100%;
}
.client-status-list li {
  display: flex;
  align-items: flex-start;

  min-height: 3em;
  border: 1px solid transparent;
  border-radius: 5px;
  box-shadow: 0 0 0 0 #a7a9ac;
  padding: 0.5em 1em;
  cursor: pointer;
}
.client-status-list li:not(:first-child) {
  margin-top: 1em;
}
.client-status-list li.enabled:hover {
  border: 1px solid #a7a9ac;
  box-shadow: 0 0 10px 0 #a7a9ac;
}
.client-status-list li.selected {
  border: 1px solid #a7a9ac;
  box-shadow: 0 0 10px 0 #a7a9ac;
}

.client-status-list li h2 {
  flex-grow: 1;
  margin: 0;
  width: 75%;

  font-weight: 100;
  line-height: 1.5em;
  transition: all 0.3s;
}

.client-status-list p.circle {
  margin: 0 0 0 1em;
  width: 1.2em;
  height: 1.2em;
  border-radius: 0.8em;
  border: 1px solid #a7a9ac;
  background-color: #fff;
}
.client-status-list p.circle-green {
  margin: 0 0 0 1em;
  width: 1.2em;
  height: 1.2em;
  border-radius: 0.8em;
  border: 1px solid #a7a9ac;
  background: #8ac640;
}

.footer {
  margin-top: 3em;
  padding-left: 1em;
}
.submit {
  max-width: 12em;
  width: 80%;
  border: 1px solid #a7a9ac;
  border-radius: 0.4em;
  padding: 0.4em 4em;
  color: #6d6e71;
  font-size: 18px;
  text-align: center;
  outline: none;
}
.submit.enabled {
  background: #8ac640;
  color: #fff;
  cursor: pointer;
}
.submit.enabled:hover {
  box-shadow: 0 0 5px 0 #a7a9ac;
}

/** Landscape mobile */
@media only screen and (orientation: landscape) and (max-height: 600px) {
  #outcome {
    padding: 2em 1em;
  }
  .top > h1 {
    text-align: left;
  }
}

/** Larger devices */
@media only screen and (min-width: 601px) and (min-height: 601px) {
  #outcome {
    justify-content: center;
    max-width: 60em;
    width: 90%;
  }

  .top > h1 {
    text-align: left;
  }

  .footer {
    margin-top: 4em;
  }
}
</style>
